import { Grid, TextField } from '@mui/material';
import { CuriosityFilterByType, isCuriosityTranslation } from '@procureai/curiosity-ui-mui';
import { useMemo } from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LanguageAutocomplete } from '~/components/LanguageAutocomplete/LanguageAutocomplete';
import { SupplierAutocomplete } from '~/components/SupplierAutocomplete';
import { supplierAutocompleteHelper } from '~/components/SupplierAutocomplete/SupplierAutocomplete.helper';
import { SupplierContactAutocomplete, SupplierContactHitId } from '~/components/SupplierContactAutocomplete';
import { SupplierLocationAutocomplete } from '~/components/SupplierLocationAutocomplete';
import { useFormFieldErrorTranslation } from '~/hooks';

import { commonInputProps } from './SupplierContactForm.common';
import { SupplierContactFormData, supplierContactFormDataDefault } from './SupplierContactForm.schema';
import { SupplierContactNameHint } from './SupplierContactNameHint';

interface SupplierContactFormProps {
  disableSupplierChange?: boolean;
  disabled?: boolean;
}

export const SupplierContactForm = ({ disableSupplierChange, disabled }: SupplierContactFormProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'SupplierContactForm' });
  const { errorToHelperText } = useFormFieldErrorTranslation();

  const { watch } = useFormContext<SupplierContactFormData>();

  const { mapContact, mapLocation } = useMemo(() => supplierAutocompleteHelper(), []);

  const watchContactId = watch('supplier.contact.internalId');
  const watchContactFirstName = watch('supplier.contact.firstName');
  const watchContactLastName = watch('supplier.contact.lastName');
  const watchSupplier = watch('supplier');
  const supplierId = watchSupplier?.internalId;

  return (
    <Grid
      container
      direction="column"
      spacing={4}
      sx={{
        justifyContent: 'flex-start',
      }}
    >
      <Grid size={12}>
        <Controller
          name="supplier"
          render={({ field, fieldState: { error } }) => {
            const { internalId } = field.value || {};
            const nestedError: FieldError | undefined = (error as any)?.internalId;
            return (
              <SupplierAutocomplete
                {...field}
                disabled={disabled || disableSupplierChange || field.disabled}
                value={internalId}
                size={commonInputProps.size}
                disableClearable={false}
                onChange={(value) => {
                  if (value && isCuriosityTranslation(value)) {
                    field.onChange({
                      internalId: value.key,
                      name: value.value,
                      externalReference: value.entity.sourceReference,
                      location: mapLocation(value.entity.locations),
                      contact: mapContact(value.entity.contacts),
                    });
                  } else {
                    field.onChange({
                      location: null,
                      contact: null,
                    });
                  }
                }}
                TextFieldProps={{
                  ...commonInputProps,
                  label: t('contact.company.procureaiSupplierId'),
                  error: !!nestedError,
                  helperText: errorToHelperText(nestedError),
                }}
              />
            );
          }}
        />
      </Grid>
      <Grid size={12}>
        <Controller
          key={watchSupplier?.internalId}
          name="supplier.location"
          render={({ field, fieldState: { error } }) => {
            const { internalId } = field.value || {};
            return (
              <SupplierLocationAutocomplete
                {...field}
                value={internalId || null}
                multiple={false}
                disableClearable={false}
                disabled={disabled || !supplierId}
                size={commonInputProps.size}
                filter={{
                  by: CuriosityFilterByType.SUPPLIER_ID,
                  value: supplierId,
                }}
                onChange={(value) => {
                  if (value && isCuriosityTranslation(value)) {
                    field.onChange({
                      internalId: value.key,
                      countryCode: value.entity.countryCode,
                      city: value.entity.city,
                      address: value.entity.address,
                      postalCode: value.entity.postalCode,
                    });
                  } else {
                    field.onChange(null);
                  }
                }}
                TextFieldProps={{
                  ...commonInputProps,
                  label: t('contact.company.chooseAddress'),
                  error: !!error,
                  helperText: errorToHelperText(error),
                }}
              />
            );
          }}
        />
      </Grid>
      <Grid size={12}>
        <Controller
          key={supplierId}
          name="supplier.contact"
          defaultValue=""
          render={({ field, fieldState: { error } }) => {
            const { internalId } = field.value || {};
            return (
              <SupplierContactAutocomplete
                {...field}
                value={internalId || null}
                multiple={false}
                disabled={disabled || !supplierId}
                size={commonInputProps.size}
                filter={{
                  by: CuriosityFilterByType.SUPPLIER_ID,
                  value: supplierId,
                }}
                onChange={(value) => {
                  if (value) {
                    if (isCuriosityTranslation(value)) {
                      field.onChange({
                        internalId: value.key,
                        email: value.entity.email,
                        language: value.entity.preferredLanguage,
                        firstName: value.entity.firstName,
                        lastName: value.entity.lastName,
                      });
                    } else {
                      // sentinel provided
                      field.onChange({
                        ...supplierContactFormDataDefault.supplier?.contact,
                        internalId: value,
                      });
                    }
                  } else {
                    field.onChange(null);
                  }
                }}
                TextFieldProps={{
                  ...commonInputProps,
                  label: t('contact.company.chooseContact'),
                  error: !!error,
                  helperText: errorToHelperText(error),
                }}
              />
            );
          }}
        />
      </Grid>
      {watchContactId ? (
        <>
          {watchContactId === SupplierContactHitId.NEW ? (
            <>
              <Grid size={12}>
                <Controller
                  key={watchContactId}
                  name="supplier.contact.firstName"
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label={t('contact.firstName')}
                      error={!!error}
                      helperText={errorToHelperText(error)}
                      {...commonInputProps}
                      {...field}
                      value={field.value || ''}
                      disabled={disabled || field.disabled}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  key={watchContactId}
                  name="supplier.contact.lastName"
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label={t('contact.lastName')}
                      error={!!error}
                      helperText={errorToHelperText(error)}
                      {...commonInputProps}
                      {...field}
                      value={field.value || ''}
                      disabled={disabled || field.disabled}
                    />
                  )}
                />
              </Grid>
            </>
          ) : null}
          <Grid size={12}>
            <Controller
              key={watchContactId}
              name="supplier.contact.email"
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label={t('contact.email')}
                  error={!!error}
                  helperText={errorToHelperText(error)}
                  autoComplete="off"
                  {...commonInputProps}
                  {...field}
                  value={field.value || ''}
                  disabled={disabled || field.disabled}
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Controller
              key={watchContactId}
              name="supplier.contact.mobile"
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label={t('contact.mobile')}
                  error={!!error}
                  helperText={errorToHelperText(error)}
                  {...commonInputProps}
                  {...field}
                  value={field.value || ''}
                  disabled={disabled || field.disabled}
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Controller
              key={watchContactId}
              name="supplier.contact.phone"
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label={t('contact.phone')}
                  error={!!error}
                  helperText={errorToHelperText(error)}
                  {...commonInputProps}
                  {...field}
                  value={field.value || ''}
                  disabled={disabled || field.disabled}
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Controller
              key={watchContactId}
              name="supplier.contact.language"
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <LanguageAutocomplete
                  {...field}
                  disabled={disabled || field.disabled}
                  value={field.value || null}
                  size={commonInputProps.size}
                  onChange={(_, items) => {
                    field.onChange(items);
                  }}
                  TextFieldProps={{
                    ...commonInputProps,
                    label: t('contact.language'),
                    error: !!error,
                    helperText: errorToHelperText(error),
                  }}
                />
              )}
            />
          </Grid>
        </>
      ) : null}
      {watchContactId ? (
        <SupplierContactNameHint firstName={watchContactFirstName} lastName={watchContactLastName} />
      ) : null}
    </Grid>
  );
};
